import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Edit2, Trash2, Check, X, AlertTriangle, Loader2,
  ChevronLeft, FolderTree, RefreshCw, CheckCircle, XCircle
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import systemRuleGroupMaintenanceService from '../../../../services/systemRuleGroupMaintenanceService';
import {
  SystemRuleGroupMaintenanceDto,
  CreateSystemRuleGroupRequest,
  UpdateSystemRuleGroupRequest,
  SystemRuleGroupFormData,
  emptyFormData,
} from '../../../../types/systemRuleGroupMaintenance';

interface SystemRuleGroupMaintenanceProps {
  onBack?: () => void;
}

type ModalMode = 'create' | 'edit' | null;

export const SystemRuleGroupMaintenance: React.FC<SystemRuleGroupMaintenanceProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // List state
  const [groups, setGroups] = useState<SystemRuleGroupMaintenanceDto[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<SystemRuleGroupMaintenanceDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedGroup, setSelectedGroup] = useState<SystemRuleGroupMaintenanceDto | null>(null);
  const [formData, setFormData] = useState<SystemRuleGroupFormData>(emptyFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<SystemRuleGroupMaintenanceDto | null>(null);

  // Load groups
  const loadGroups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await systemRuleGroupMaintenanceService.getAll();
      setGroups(data);
      setFilteredGroups(data);
    } catch (err: any) {
      setError(err.message || t('manager.modules.systemRuleGroups.list.errors.loadFailed', {}, 'Kunne ikke hente regelgrupper'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // Filter groups when search term changes
  useEffect(() => {
    let filtered = groups;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        g =>
          g.lng1.toLowerCase().includes(term) ||
          (g.lng2 && g.lng2.toLowerCase().includes(term)) ||
          g.ruleGroupID.toString().includes(term)
      );
    }

    setFilteredGroups(filtered);
  }, [groups, searchTerm]);

  // Open modal for create
  const handleCreate = () => {
    setSelectedGroup(null);
    setFormData(emptyFormData);
    setModalMode('create');
    setFormError(null);
  };

  // Open modal for edit
  const handleEdit = (group: SystemRuleGroupMaintenanceDto) => {
    setSelectedGroup(group);
    setFormData({
      lng1: group.lng1,
      lng2: group.lng2 || '',
      activeYesNo: group.activeYesNo,
    });
    setModalMode('edit');
    setFormError(null);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalMode(null);
    setSelectedGroup(null);
    setFormData(emptyFormData);
    setFormError(null);
  };

  // Save group
  const handleSave = async () => {
    if (!formData.lng1.trim()) {
      setFormError(t('manager.modules.systemRuleGroups.form.errors.nameRequired', {}, 'Gruppenavn er pakrevd'));
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      if (modalMode === 'create') {
        const request: CreateSystemRuleGroupRequest = {
          lng1: formData.lng1,
          lng2: formData.lng2 || undefined,
          activeYesNo: formData.activeYesNo,
        };
        await systemRuleGroupMaintenanceService.create(request);
      } else if (modalMode === 'edit' && selectedGroup) {
        const request: UpdateSystemRuleGroupRequest = {
          lng1: formData.lng1,
          lng2: formData.lng2 || undefined,
          activeYesNo: formData.activeYesNo,
        };
        await systemRuleGroupMaintenanceService.update(selectedGroup.ruleGroupID, request);
      }

      await loadGroups();
      handleCloseModal();
    } catch (err: any) {
      setFormError(err.message || t('manager.modules.systemRuleGroups.form.errors.saveFailed', {}, 'Kunne ikke lagre regelgruppe'));
    } finally {
      setIsSaving(false);
    }
  };

  // Delete group
  const handleDeleteClick = (group: SystemRuleGroupMaintenanceDto) => {
    setGroupToDelete(group);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!groupToDelete) return;

    setIsDeleting(true);
    setFormError(null);
    try {
      await systemRuleGroupMaintenanceService.delete(groupToDelete.ruleGroupID);
      await loadGroups();
      setShowDeleteConfirm(false);
      setGroupToDelete(null);
    } catch (err: any) {
      setFormError(err.message || t('manager.modules.systemRuleGroups.list.errors.deleteFailed', {}, 'Kunne ikke slette regelgruppe'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('common.buttons.back', {}, 'Tilbake')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-3">
              <FolderTree className="w-6 h-6 text-gray-600" />
              <h1 className="text-xl font-semibold text-gray-900">{t('manager.modules.systemRuleGroups.list.title', {}, 'Regelgrupper')}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={loadGroups}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('common.buttons.refresh', {}, 'Oppdater')}
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('manager.modules.systemRuleGroups.list.createButton', {}, 'Ny regelgruppe')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('manager.modules.systemRuleGroups.list.searchPlaceholder', {}, 'Sok etter regelgrupper...')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <span className="text-sm text-gray-500">
            {t('manager.modules.systemRuleGroups.list.countSummary', { '0': filteredGroups.length, '1': groups.length }, `${filteredGroups.length} av ${groups.length} regelgrupper`)}
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        {formError && !modalMode && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertTriangle className="w-5 h-5" />
            <span>{formError}</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRuleGroups.table.id', {}, 'ID')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.fields.name', {}, 'Navn')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRuleGroups.table.ruleCount', {}, 'Antall regler')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.fields.status', {}, 'Status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRuleGroups.table.lastUpdate', {}, 'Sist oppdatert')}</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('manager.modules.systemRuleGroups.table.actions', {}, 'Handlinger')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredGroups.map(group => (
                  <tr
                    key={group.ruleGroupID}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleEdit(group)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{group.ruleGroupID}</td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">{group.lng1}</div>
                      {group.lng2 && <div className="text-xs text-gray-500">{group.lng2}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        group.ruleCount > 0
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {group.ruleCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {group.activeYesNo ? (
                        <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          {t('manager.modules.systemRuleGroups.status.active', {}, 'Aktiv')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400 text-sm">
                          <XCircle className="w-4 h-4" />
                          {t('manager.modules.systemRuleGroups.status.inactive', {}, 'Inaktiv')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {group.lastUpdate
                        ? new Date(group.lastUpdate).toLocaleString('nb-NO')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleEdit(group)}
                          className="p-1.5 hover:bg-blue-100 rounded text-blue-600"
                          title={t('common.buttons.edit', {}, 'Rediger')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(group)}
                          className={`p-1.5 rounded ${
                            group.ruleCount > 0
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'hover:bg-red-100 text-red-600'
                          }`}
                          title={group.ruleCount > 0 ? t('manager.modules.systemRuleGroups.table.deleteDisabledTooltip', {}, 'Kan ikke slette - har tilknyttede regler') : t('common.buttons.delete', {}, 'Slett')}
                          disabled={group.ruleCount > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredGroups.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                      {t('manager.modules.systemRuleGroups.list.empty', {}, 'Ingen regelgrupper funnet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {modalMode === 'create'
                  ? t('manager.modules.systemRuleGroups.modal.createTitle', {}, 'Ny regelgruppe')
                  : t('manager.modules.systemRuleGroups.modal.editTitle', {}, 'Rediger regelgruppe')}
                {selectedGroup && t('manager.modules.systemRuleGroups.modal.idSuffix', { '0': selectedGroup.ruleGroupID }, ` (ID: ${selectedGroup.ruleGroupID})`)}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {formError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="space-y-4">
                {/* Group Name Norwegian */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.systemRuleGroups.modal.nameNbLabel', {}, 'Gruppenavn (norsk) *')}
                  </label>
                  <input
                    type="text"
                    value={formData.lng1}
                    onChange={e => setFormData(prev => ({ ...prev, lng1: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('manager.modules.systemRuleGroups.modal.namePlaceholder', {}, 'Skriv inn gruppenavn...')}
                    autoFocus
                  />
                </div>

                {/* Group Name English */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('manager.modules.systemRuleGroups.modal.nameEnLabel', {}, 'Gruppenavn (engelsk)')}
                  </label>
                  <input
                    type="text"
                    value={formData.lng2}
                    onChange={e => setFormData(prev => ({ ...prev, lng2: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={t('manager.modules.systemRuleGroups.modal.nameEnPlaceholder', {}, 'Enter group name...')}
                  />
                </div>

                {/* Active checkbox */}
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.activeYesNo}
                      onChange={e => setFormData(prev => ({ ...prev, activeYesNo: e.target.checked }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{t('manager.modules.systemRuleGroups.status.active', {}, 'Aktiv')}</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Check className="w-4 h-4" />
                )}
                {isSaving ? t('common.status.saving', {}, 'Lagrer...') : t('common.buttons.save', {}, 'Lagre')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && groupToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{t('manager.modules.systemRuleGroups.delete.title', {}, 'Bekreft sletting')}</h3>
            </div>

            <p className="text-gray-600 mb-6">
              {t('manager.modules.systemRuleGroups.delete.message', { '0': groupToDelete.lng1 }, `Er du sikker pa at du vil slette regelgruppen "${groupToDelete.lng1}"? Denne handlingen kan ikke angres.`)}
            </p>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4" />
                <span>{formError}</span>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setGroupToDelete(null);
                  setFormError(null);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('common.buttons.cancel', {}, 'Avbryt')}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 transition-colors"
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {isDeleting ? t('manager.modules.systemRuleGroups.delete.deleting', {}, 'Sletter...') : t('common.buttons.delete', {}, 'Slett')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemRuleGroupMaintenance;
